import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTopics } from '../services/topics';
import { setTitle } from '../utils/environment';

const Topics = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTopics({
        limit: 50,
        unread_only: true
      });
      setTopics(response.topics || []);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleTopicClick = (topicId) => {
    navigate(`/topics/${topicId}`);
  };

  const formatScore = (score) => {
    return Math.round(score * 100);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-blue-600 bg-blue-100';
    if (score >= 0.4) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const formatRecency = (days) => {
    if (days < 1) return 'today';
    if (days < 2) return '1 day ago';
    if (days < 7) return `${Math.round(days)} days ago`;
    if (days < 30) return `${Math.round(days / 7)} weeks ago`;
    return `${Math.round(days / 30)} months ago`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  useEffect(() => {
    setTitle('Topics');
    fetchTopics();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="mr-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Topics</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {topics.length} AI-generated topic clusters
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">


        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="spinner w-8 h-8 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading topics...</p>
            </div>
          </div>
        ) : topics.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No topics found</h2>
            <p className="text-gray-600">
              Topics are automatically generated when you have unread articles. Check back later or visit your dashboard to see if new topics have been created.
            </p>
          </div>
        ) : (
          /* Topics Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((topic) => (
              <div
                key={topic.id}
                onClick={() => handleTopicClick(topic.id)}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg cursor-pointer transition-all border-l-4 border-blue-500"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800 leading-6 pr-2">
                    {topic.label}
                  </h3>
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(topic.final_score)}`}>
                    {formatScore(topic.final_score)}%
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <span className="text-blue-500">📄</span>
                    <span className="ml-1">{topic.unread_count} unread</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-green-500">📚</span>
                    <span className="ml-1">{topic.cluster_size} total</span>
                  </div>
                </div>

                <div className="text-sm text-gray-500 space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Average age:</span>
                    <span>{formatRecency(topic.avg_recency_days)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Created:</span>
                    <span>{formatDate(topic.created_at)}</span>
                  </div>
                  {topic.avg_feedback_score !== 0 && (
                    <div className="flex items-center justify-between">
                      <span>Avg feedback:</span>
                      <span>
                        {topic.avg_feedback_score > 0 ? '👍' : '👎'}
                        {Math.abs(topic.avg_feedback_score).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-400 flex items-center justify-between">
                    <span>{topic.algorithm}</span>
                    {topic.topic_metadata?.silhouette_score && (
                      <span>Quality: {(topic.topic_metadata.silhouette_score * 100).toFixed(1)}%</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Topics;